/**
 * NotificationModal
 *
 * Single Radix Dialog rendered once at app level.
 * Reads all state from NotificationContext.
 *
 * Two modes:
 * - Detail: rich gradient hero for a single new notification (socket-triggered)
 * - List: compact scrollable history (~4 items visible)
 */

import { useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Bell,
  Megaphone,
  Calendar,
  MessageSquare,
  Info,
  Loader2,
  ExternalLink,
  X,
  ChevronRight,
  List,
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNotifications, type AppNotification } from '@/contexts/NotificationContext';

// ─── Icon metadata by type ───────────────────────────────────────────────────

function notificationMeta(type: string) {
  if (type === 'announcement')
    return {
      icon: Megaphone, color: 'text-white',
      bg: 'bg-gradient-to-br from-primary to-primary/80',
      heroBg: 'from-[hsl(216,100%,22%)] via-[hsl(216,80%,30%)] to-[hsl(195,90%,40%)]',
      label: 'Announcement',
    };
  if (type === 'session_reminder')
    return {
      icon: Calendar, color: 'text-white',
      bg: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
      heroBg: 'from-emerald-600 via-emerald-500 to-teal-400',
      label: 'Session Reminder',
    };
  if (type === 'message_notification')
    return {
      icon: MessageSquare, color: 'text-white',
      bg: 'bg-gradient-to-br from-blue-500 to-blue-600',
      heroBg: 'from-blue-600 via-blue-500 to-cyan-400',
      label: 'New Message',
    };
  return {
    icon: Info, color: 'text-white',
    bg: 'bg-gradient-to-br from-slate-400 to-slate-500',
    heroBg: 'from-slate-600 via-slate-500 to-slate-400',
    label: 'Notification',
  };
}

// ─── Detail view (single notification) ───────────────────────────────────────

function NotificationDetailView({
  notification,
  onRead,
  onShowList,
  onClose,
}: {
  notification: AppNotification;
  onRead: (id: string) => void;
  onShowList: () => void;
  onClose: () => void;
}) {
  const meta = notificationMeta(notification.type);
  const Icon = meta.icon;

  // Mark as read on mount
  useEffect(() => {
    if (!notification.isRead) {
      onRead(notification.id);
    }
  }, [notification.id]);

  const handleAction = () => {
    if (notification.deepLinkUrl) {
      window.location.href = notification.deepLinkUrl;
    }
    onClose();
  };

  return (
    <>
      {/* Compact gradient header with icon + label + close */}
      <div className={cn('relative bg-gradient-to-br text-white px-5 py-4 flex items-center gap-3.5 flex-shrink-0', meta.heroBg)}>
        <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-md">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-white/60">
            {meta.label}
          </span>
          {notification.title && (
            <h3 className="text-base font-bold text-white leading-snug mt-0.5 truncate">
              {notification.title}
            </h3>
          )}
        </div>
        <Dialog.Close asChild>
          <button className="h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors flex-shrink-0">
            <span className="sr-only">Close</span>
            <X className="h-4 w-4 text-white" />
          </button>
        </Dialog.Close>
      </div>

      {/* Message content area */}
      <div className="px-5 py-5 flex-1 flex flex-col bg-muted/30">
        <div className="bg-card rounded-xl border border-border/60 shadow-sm p-5 flex-1">
          <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
            {notification.content}
          </p>
          <p className="text-xs text-muted-foreground/50 mt-4 font-medium">
            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
          </p>

          {notification.deepLinkUrl && (
            <Button
              className="w-full mt-5 rounded-xl h-10 font-semibold gap-2"
              onClick={handleAction}
            >
              View Details
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="flex items-center justify-end mt-3 px-1">
          <button
            onClick={onShowList}
            className="text-xs text-muted-foreground hover:text-foreground font-medium flex items-center gap-1.5 ml-auto transition-colors"
          >
            <List className="h-3.5 w-3.5" />
            See all notifications
          </button>
        </div>
      </div>
    </>
  );
}

// ─── List item ───────────────────────────────────────────────────────────────

function NotificationItem({ notification }: { notification: AppNotification }) {
  const handleClick = () => {
    if (notification.deepLinkUrl) {
      window.location.href = notification.deepLinkUrl;
    }
  };

  const meta = notificationMeta(notification.type);
  const Icon = meta.icon;

  return (
    <div className="px-3 pt-2 first:pt-3 last:pb-3">
      <button
        onClick={handleClick}
        className="w-full text-left p-3.5 flex items-start gap-3.5 rounded-xl transition-all bg-card border border-border/60 shadow-sm hover:shadow-md"
      >
        <div className="flex-shrink-0">
          <div className={cn(
            'w-10 h-10 rounded-xl flex items-center justify-center shadow-sm',
            meta.bg
          )}>
            <Icon className={cn('h-[18px] w-[18px]', meta.color)} />
          </div>
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          {notification.title && (
            <p className="text-[13px] leading-snug font-bold text-foreground/80">
              {notification.title}
            </p>
          )}
          <p className={cn(
            'text-[13px] leading-relaxed',
            notification.title ? 'mt-0.5 text-muted-foreground' : 'text-muted-foreground'
          )}>
            {notification.content}
          </p>
          <p className="text-[11px] text-muted-foreground/50 mt-1.5 font-medium">
            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
          </p>
        </div>

        {notification.deepLinkUrl && (
          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/30 flex-shrink-0 mt-1.5" />
        )}
      </button>
    </div>
  );
}

// ─── Main modal ──────────────────────────────────────────────────────────────

export function NotificationModal() {
  const {
    open,
    viewMode,
    detailNotification,
    notifications,
    isLoading,
    isFetching,
    total,
    hasMore,
    closeModal,
    loadMore,
    markRead,
    switchToList,
  } = useNotifications();

  return (
    <Dialog.Root open={open} onOpenChange={(value) => { if (!value) closeModal(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 md:bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed z-50 rounded-xl flex flex-col overflow-hidden',
            'shadow-2xl border border-border',
            'bg-background',
            'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-[calc(100%-24px)] max-w-[500px] md:max-w-[550px]',
            'max-h-[85vh]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
          )}
        >
          {viewMode === 'detail' && detailNotification ? (
            <>
              <Dialog.Title className="sr-only">Notification</Dialog.Title>
              <NotificationDetailView
                notification={detailNotification}
                onRead={(id) => markRead([id])}
                onShowList={switchToList}
                onClose={closeModal}
              />
            </>
          ) : (
            <>
              {/* List header */}
              <div className="bg-gradient-navy text-primary-foreground px-6 py-4 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                    <Bell className="h-4 w-4" />
                  </div>
                  <div>
                    <Dialog.Title className="font-semibold text-lg leading-tight">Notifications</Dialog.Title>
                    <p className="text-xs text-primary-foreground/60 mt-0.5">
                      {total > 0 ? `${total} total` : 'All caught up'}
                    </p>
                  </div>
                </div>
                <Dialog.Close asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-primary-foreground/80 hover:text-primary-foreground hover:bg-card/20 rounded-lg">
                    <span className="sr-only">Close</span>
                    <X className="h-4 w-4" />
                  </Button>
                </Dialog.Close>
              </div>

              {/* List body — max ~4 items visible, then scroll */}
              <div className="max-h-[360px] overflow-y-auto bg-muted/40">
                {isLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-5 w-5 animate-spin text-primary/40" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center px-8">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center mb-5 shadow-md">
                      <Bell className="h-6 w-6 text-white/80" />
                    </div>
                    <p className="font-bold text-foreground/70 text-sm">No notifications yet</p>
                    <p className="text-xs text-muted-foreground/50 mt-1.5 max-w-[240px] leading-relaxed">
                      Session reminders and announcements will appear here.
                    </p>
                  </div>
                ) : (
                  <div>
                    {notifications.map((n) => (
                      <NotificationItem key={n.id} notification={n} />
                    ))}
                    {hasMore && (
                      <div className="px-4 pb-3 pt-1 flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-xl text-xs h-8 w-full max-w-[200px] border-border/50 text-muted-foreground hover:text-foreground"
                          onClick={loadMore}
                          disabled={isFetching}
                        >
                          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load more'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
